import React, { useState, useEffect, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, Shield, ShieldOff, Edit2, X, Check, Loader2, Crown, Eye, AlertTriangle, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders, useAuth } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';

import { BACKEND_URL } from '../config/api';

// Derive tier from paid item slots
const getTierFromPaidItems = (paidItems) => {
  if (paidItems <= 0) return 'Free';
  if (paidItems < 200) return 'Starter';
  if (paidItems < 500) return 'Crafter';
  return 'Artisan';
};

const getTierIdFromPaidItems = (paidItems) => {
  if (paidItems <= 0) return 'free';
  if (paidItems < 200) return 'starter';
  if (paidItems < 500) return 'crafter';
  return 'artisan';
};

function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    is_admin: user.is_admin || false,
    is_unlimited: user.is_unlimited || false,
    paid_item_slots: user.paid_item_slots || 0,
    billing_status: user.billing_status || 'free',
  });
  const [saving, setSaving] = useState(false);

  const freeGrant = user.free_item_base_grant || 20;
  const totalCapacity = form.paid_item_slots + freeGrant;

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        ...form,
        subscription_tier: getTierIdFromPaidItems(form.paid_item_slots),
        stash_item_limit: totalCapacity,
      };
      
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${user.user_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSave)
      });
      if (res.ok) {
        onSaved();
        onClose();
      }
    } catch (err) {
      console.error('Failed to update user:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-charcoal/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-beige">
          <h3 className="font-medium text-charcoal">Edit User</h3>
          <button onClick={onClose} className="text-gray-soft hover:text-charcoal" data-testid="edit-user-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* User info header */}
          <div className="flex items-center gap-3 pb-4 border-b border-beige">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-sage-light flex items-center justify-center text-sage-dark font-medium">
                {user.name?.charAt(0) || '?'}
              </div>
            )}
            <div>
              <p className="font-medium text-charcoal text-sm">{user.name}</p>
              <p className="text-xs text-gray-soft">{user.email}</p>
            </div>
          </div>

          {/* Admin Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-charcoal">Admin Access</p>
              <p className="text-xs text-gray-soft">Grant full admin privileges</p>
            </div>
            <button
              onClick={() => setForm(f => ({ ...f, is_admin: !f.is_admin }))}
              className={`relative w-12 h-6 rounded-full transition-colors ${form.is_admin ? 'bg-charcoal' : 'bg-beige'}`}
              data-testid="toggle-admin"
            >
              <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_admin ? 'translate-x-6' : 'translate-x-0.5'}`}>
                {form.is_admin && <Check size={12} className="text-charcoal absolute top-1 left-1" />}
              </div>
            </button>
          </div>

          {/* Unlimited Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-amber-600">Unlimited Stash</p>
              <p className="text-xs text-gray-soft">Bypass all capacity limits</p>
            </div>
            <button
              onClick={() => setForm(f => ({ ...f, is_unlimited: !f.is_unlimited }))}
              className={`relative w-12 h-6 rounded-full transition-colors ${form.is_unlimited ? 'bg-amber-400' : 'bg-beige'}`}
              data-testid="toggle-unlimited"
            >
              <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_unlimited ? 'translate-x-6' : 'translate-x-0.5'}`}>
                {form.is_unlimited && <Crown size={12} className="text-amber-500 absolute top-1 left-1" />}
              </div>
            </button>
          </div>

          {/* Paid Item Slots */}
          <div>
            <label className="text-sm font-medium text-charcoal block mb-1.5">Paid Item Slots</label>
            <input
              type="number"
              min={0}
              value={form.paid_item_slots}
              onChange={e => setForm(f => ({ ...f, paid_item_slots: parseInt(e.target.value) || 0 }))}
              className="w-full px-3 py-2 border border-beige rounded-lg text-sm focus:outline-none focus:border-sage"
              data-testid="edit-paid-slots"
            />
            <p className="text-xs text-gray-soft mt-1">
              Total capacity: {totalCapacity} ({form.paid_item_slots} paid + {freeGrant} free) | Usage: {user.stash_item_count || 0} items
            </p>
          </div>

          {/* Billing Status */}
          <div>
            <label className="text-sm font-medium text-charcoal block mb-1.5">Billing Status</label>
            <select
              value={form.billing_status}
              onChange={e => setForm(f => ({ ...f, billing_status: e.target.value }))}
              className="w-full px-3 py-2 border border-beige rounded-lg text-sm focus:outline-none focus:border-sage"
              data-testid="edit-billing-status"
            >
              <option value="free">Free</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="exempt">Exempt</option>
            </select>
          </div>

          {/* Subscription Tier (Read-only - derived from paid items) */}
          <div>
            <label className="text-sm font-medium text-charcoal block mb-1.5">Subscription Tier</label>
            <div className="w-full px-3 py-2 border border-beige rounded-lg text-sm bg-cream text-gray-soft">
              {getTierFromPaidItems(form.paid_item_slots)}
              <span className="text-xs ml-2">(based on paid items)</span>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-beige flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-soft hover:text-charcoal transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm bg-charcoal text-white rounded-lg hover:bg-charcoal/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="save-user-btn"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

function ImpersonateConfirmModal({ user, onConfirm, onClose, loading }) {
  return (
    <div className="fixed inset-0 bg-charcoal/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl" data-testid="impersonate-confirm-modal">
        <div className="p-6">
          {/* Warning icon */}
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={24} className="text-amber-600" />
          </div>

          <h3 className="text-lg font-medium text-charcoal text-center mb-2">
            Impersonate User
          </h3>

          {/* User info */}
          <div className="flex items-center gap-3 bg-cream rounded-lg p-3 mb-4">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-sage-light flex items-center justify-center text-sage-dark font-medium">
                {user.name?.charAt(0) || '?'}
              </div>
            )}
            <div className="min-w-0">
              <p className="font-medium text-charcoal text-sm truncate">{user.name}</p>
              <p className="text-xs text-gray-soft truncate">{user.email}</p>
            </div>
          </div>

          <p className="text-sm text-charcoal mb-3">
            You will see the app exactly as <strong>{user.name}</strong> sees it. Any changes you make (adding or editing items) will affect their account.
          </p>

          {/* Safety info */}
          <div className="bg-sage-light/30 rounded-lg p-3 text-xs text-charcoal space-y-1.5">
            <p className="font-medium text-sage-dark">Safety protections active:</p>
            <p>You <strong>cannot</strong> delete their stash items</p>
            <p>You <strong>cannot</strong> change their subscription (upgrade, downgrade, or cancel)</p>
            <p>Session auto-expires after 1 hour</p>
            <p>This session is logged for audit</p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-beige flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm text-gray-soft hover:text-charcoal transition-colors"
            data-testid="impersonate-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="impersonate-confirm-btn"
          >
            {loading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Eye size={14} />
            )}
            View as {user.name?.split(' ')[0] || 'User'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [confirmImpersonate, setConfirmImpersonate] = useState(null); // user object to confirm
  const [impersonating, setImpersonating] = useState(false);
  const navigate = useNavigate();
  const { user: adminUser, startImpersonation } = useAuth();

  const handleImpersonateConfirm = async () => {
    if (!confirmImpersonate) return;
    setImpersonating(true);
    try {
      await startImpersonation(confirmImpersonate.user_id);
      navigate('/stash');
    } catch (err) {
      alert(err.message || 'Failed to start impersonation');
      setImpersonating(false);
      setConfirmImpersonate(null);
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '15' });
      if (search) params.set('search', search);
      const res = await fetch(`${BACKEND_URL}/api/admin/users?${params}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.clone().json();
        setUsers(data.users);
        setTotal(data.total);
        setPages(data.pages);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // CSV export for a user's stash
  const handleExportStash = async (u) => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/users/${u.user_id}/stash.csv`, {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        const err = await resp.text();
        alert(`Export failed (${resp.status}): ${err.slice(0, 200)}`);
        return;
      }
      const blob = await resp.blob();
      const dispo = resp.headers.get('content-disposition') || '';
      const match = dispo.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || `stash_${u.email || u.user_id}.csv`;
      const rowCount = resp.headers.get('x-row-count');
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(downloadUrl);
      if (rowCount === '0') {
        alert(`Export downloaded but the user has 0 stash items in this database. Check that you're logged into the right environment.`);
      }
    } catch (e) {
      console.error('Export stash error:', e);
      alert('Export failed. Check the console for details.');
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-users">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-2xl font-medium text-charcoal">Users</h1>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-soft" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="pl-9 pr-4 py-2 border border-beige rounded-lg text-sm w-full sm:w-72 focus:outline-none focus:border-sage bg-white"
              data-testid="user-search-input"
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl border border-beige overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-beige bg-cream/50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-soft uppercase tracking-wide">User</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-soft uppercase tracking-wide hidden sm:table-cell">Tier</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-soft uppercase tracking-wide hidden md:table-cell">Stash</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-soft uppercase tracking-wide">Role</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-soft uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-beige last:border-0">
                      <td colSpan={5} className="px-4 py-4"><div className="h-5 bg-beige rounded animate-pulse" /></td>
                    </tr>
                  ))
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-gray-soft text-sm">No users found</td>
                  </tr>
                ) : (
                  users.map(u => (
                    <tr key={u.user_id} className="border-b border-beige last:border-0 hover:bg-cream/30 transition-colors" data-testid={`user-row-${u.user_id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full flex-shrink-0" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-sage-light flex items-center justify-center flex-shrink-0 text-sage-dark text-sm font-medium">
                              {u.name?.charAt(0) || '?'}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-charcoal truncate">{u.name}</p>
                            <p className="text-xs text-gray-soft truncate">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        {u.is_unlimited ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">Unlimited</span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-cream text-charcoal capitalize">{u.subscription_tier || 'free'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-sm text-charcoal">
                          {u.stash_item_count || 0} / {u.is_unlimited ? '∞' : ((u.paid_item_slots || 0) + (u.free_item_base_grant || 20))}
                        </span>
                        {(u.referral_count || 0) > 0 && (
                          <span className="ml-2 text-xs text-sage" title={`${u.referral_count} referrals`}>{u.referral_count} ref</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {u.is_admin && (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-charcoal text-white">
                              <Shield size={10} /> Admin
                            </span>
                          )}
                          {u.is_unlimited && (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-400 text-white" title="Unlimited stash">
                              <Crown size={10} />
                            </span>
                          )}
                          {!u.is_admin && !u.is_unlimited && (
                            <span className="text-xs text-gray-soft">User</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {!u.is_admin && u.user_id !== adminUser?.user_id && (
                            <button
                              onClick={() => setConfirmImpersonate(u)}
                              className="p-1.5 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-all"
                              title={`View as ${u.name}`}
                              data-testid={`impersonate-user-${u.user_id}`}
                            >
                              <Eye size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => handleExportStash(u)}
                            className="p-1.5 text-gray-soft hover:text-sage hover:bg-sage/10 rounded-lg transition-all"
                            title={`Export ${u.name}'s stash as CSV`}
                            data-testid={`export-stash-${u.user_id}`}
                          >
                            <Download size={16} />
                          </button>
                          <button
                            onClick={() => setEditingUser(u)}
                            className="p-1.5 text-gray-soft hover:text-charcoal hover:bg-cream rounded-lg transition-all"
                            data-testid={`edit-user-${u.user_id}`}
                          >
                            <Edit2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-beige">
              <p className="text-xs text-gray-soft">{total} users total</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1 rounded hover:bg-cream disabled:opacity-50 transition-colors"
                  data-testid="users-prev-page"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="text-xs text-charcoal">{page} / {pages}</span>
                <button
                  onClick={() => setPage(p => Math.min(pages, p + 1))}
                  disabled={page === pages}
                  className="p-1 rounded hover:bg-cream disabled:opacity-50 transition-colors"
                  data-testid="users-next-page"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Edit Modal */}
        {editingUser && (
          <EditUserModal
            user={editingUser}
            onClose={() => setEditingUser(null)}
            onSaved={fetchUsers}
          />
        )}

        {/* Impersonate Confirmation Modal */}
        {confirmImpersonate && (
          <ImpersonateConfirmModal
            user={confirmImpersonate}
            loading={impersonating}
            onConfirm={handleImpersonateConfirm}
            onClose={() => { setConfirmImpersonate(null); setImpersonating(false); }}
          />
        )}
      </div>
    </AdminLayout>
  );
}
